import { useCallback, useMemo, useState } from 'react';

import { noop } from '@frontend/utils';

import { buildFieldSuggestionAnnotations } from 'modules/common/viewer/pdfViewer/annotationCommands/suggestionUtils';
import type { SuggestionAnnotationRaw } from 'modules/common/viewer/pdfViewer/annotationCommands/types';
import { useExtractedTableAnnotations } from 'modules/common/viewer/pdfViewer/hooks/useExtractedTableAnnotations';
import {
  ExtractionVisibilityContext,
  type ExtractionVisibilityContextValue,
} from 'modules/common/viewer/pdfViewer/interactions/ExtractionVisibilityContext';
import {
  EMPTY_MODE_CONTEXT,
  getVisualPropertiesFromDisplayState,
} from 'modules/common/viewer/pdfViewer/interactions/annotationModeConfig';
import { buildAnnotationFindingsIndex } from 'modules/common/viewer/pdfViewer/interactions/buildAnnotationFindingsIndex';
import { useAnnotationOrchestration } from 'modules/common/viewer/pdfViewer/interactions/useAnnotationOrchestration';
import { useCroppedTableInteractions } from 'modules/extractedTables/hooks/useCroppedTableInteractions';

import { SFV_MODE_CONFIG } from './sfvAnnotationModeConfig';
import { useInferenceInfo } from './useInferenceInfo';

type SfvPdfInteractionsProviderProps = {
  children: React.ReactNode;
};

const modeConfig = SFV_MODE_CONFIG;
const modeContext = EMPTY_MODE_CONTEXT;
const whiteoutCategories = new Set<string>();
const toggleWhiteoutCategory = noop;
const isWhiteout = () => false;

/**
 * Standalone File Viewer PDF interactions provider.
 *
 * Provides ExtractionVisibilityContext for cross-viewer components.
 */
export const SfvPdfInteractionsProvider = ({
  children,
}: SfvPdfInteractionsProviderProps) => {
  const [showAllExtractions, setShowAllExtractionsState] =
    useState<boolean>(true);

  const toggleShowAllExtractions = useCallback(() => {
    setShowAllExtractionsState((previous) => !previous);
  }, []);

  const { suggestionsList, extractedTables } = useInferenceInfo();

  const { extractedTableAnnotations } = useExtractedTableAnnotations({
    extractedTables,
  });

  const suggestionAnnotationsList = useMemo(() => {
    if (suggestionsList.data) {
      return buildFieldSuggestionAnnotations(suggestionsList.data);
    } else {
      return [];
    }
  }, [suggestionsList.data]);

  const allAnnotations = useMemo(() => {
    return [...suggestionAnnotationsList, ...extractedTableAnnotations];
  }, [suggestionAnnotationsList, extractedTableAnnotations]);

  // --- Findings index (shared pure function) ---
  const annotationFindingsIndex = useMemo(
    () =>
      buildAnnotationFindingsIndex({
        suggestionAnnotations: suggestionAnnotationsList,
        extractedTableAnnotations,
      }),
    [suggestionAnnotationsList, extractedTableAnnotations],
  );

  // --- Annotation orchestration (shared hook) ---
  const isAnnotHidden = useCallback(
    (annot: SuggestionAnnotationRaw) => {
      if (!showAllExtractions) return true;
      return !modeConfig.isVisible(annot, modeContext);
    },
    [showAllExtractions],
  );

  const getAnnotVisualProps = useCallback((annot: SuggestionAnnotationRaw) => {
    const displayState = modeConfig.getDisplayState(annot, modeContext);
    return getVisualPropertiesFromDisplayState(displayState, annot);
  }, []);

  useAnnotationOrchestration({
    allAnnotations,
    isHidden: isAnnotHidden,
    getVisualProps: getAnnotVisualProps,
    isWhiteout,
  });

  useCroppedTableInteractions();

  const value = useMemo<ExtractionVisibilityContextValue>(
    () => ({
      state: {
        showAllExtractions,
        whiteoutCategories,
        annotationFindingsIndex,
      },
      actions: {
        toggleShowAllExtractions,
        toggleWhiteoutCategory,
      },
    }),
    [showAllExtractions, annotationFindingsIndex, toggleShowAllExtractions],
  );

  return (
    <ExtractionVisibilityContext.Provider value={value}>
      {children}
    </ExtractionVisibilityContext.Provider>
  );
};
